import React, { useMemo } from 'react';

import { ItemsTable } from 'ui/components/Table/ItemsTable';
import { Pagination } from 'services/search';
import { arrayHasAllDeletedObjects } from 'helpers';
import { PermissionType } from 'services/permissions';
import { UomSearchResultsProps } from './types';
import { UOM_COLUMNS } from '../../consts';
import { createUomsSearchActionBarOptions } from './consts';
import { default as UomRow } from './UomRow';
import FBOTitleBar from 'ui/theme/components/FBOTitleBar/FBOTitleBar';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import FBOMenuButton from 'ui/theme/components/FBOMenuButton/FBOMenuButton';
import { useSelector } from 'react-redux';
import { getActiveUser } from 'services/userV2';
import { activeUserHasPermission } from 'services/userV2/redux/selectors';
import { useFlags } from 'helpers/useFlags';

const UomSearchResults: React.FC<UomSearchResultsProps> = (props) => {
  const {
    uoms,
    activeUomId,
    handleUomClick,
    onAddNewPress,
    pagination,
    activeDate,
    onPaginationChange,
    isLoadingUoms,
    onUomsPageAction,
  } = props;

  const activeUser = useSelector(getActiveUser);
  const flags = useFlags();
  const hasImportPermission = useSelector(
    activeUserHasPermission([PermissionType.UomEdit, PermissionType.UomCreate])
  );

  const hasExportPermission = useSelector(
    activeUserHasPermission([
      PermissionType.UomView,
      PermissionType.UomEdit,
      PermissionType.UomCreate,
    ])
  );

  const csvImportDisabled =
    flags.newUserEndpoint && !(activeUser.user?.admin || hasImportPermission);
  const csvExportDisabled =
    flags.newUserEndpoint && !(activeUser.user?.admin || hasExportPermission);

  const allDeletedUoms = useMemo(() => arrayHasAllDeletedObjects(uoms), [uoms]);

  const handlePaginationChange = (newPagination: Pagination) => {
    onPaginationChange(newPagination);
  };

  const alwaysOnColumns = useMemo(
    () =>
      UOM_COLUMNS(activeDate).filter(
        (c) => c.title === 'Name' || c.title === 'Abbreviation'
      ),
    [activeDate]
  );

  return (
    <>
      <FBOTitleBar title="Unit of Measure">
        {activeUomId !== -1 && (
          <FBOButton
            style={{ marginRight: '8px' }}
            variant={!activeUomId ? 'primary' : 'secondary'}
            color="positive"
            size="medium"
            icon="FBOAddCircle"
            onClick={onAddNewPress}
            data-qa="uom-new"
            permissions={[PermissionType.UomCreate]}
          >
            {!activeUomId ? 'Add New' : ''}
          </FBOButton>
        )}
        <FBOMenuButton
          variant="secondary"
          items={createUomsSearchActionBarOptions(
            onUomsPageAction,
            uoms.length,
            csvImportDisabled,
            csvExportDisabled
          )}
          data-qa="uom-page-three-dot-menu"
        />
      </FBOTitleBar>

      <ItemsTable
        data={uoms}
        columns={activeUomId ? alwaysOnColumns : UOM_COLUMNS(activeDate)}
        activeId={activeUomId}
        onItemClick={handleUomClick}
        selectableItems={false}
        isLoadingData={isLoadingUoms}
        RenderCustomRow={UomRow}
        disableSelection={allDeletedUoms}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        dataQa="uoms-table"
        showEnhancedTablePaginationActions={!Boolean(activeUomId)}
      />
    </>
  );
};

export default UomSearchResults;
